/**
 * SINGLE CENTRALIZED ROUTE CONFIGURATION
 * 
 * This is the ONLY place where routes should be defined.
 * All route definitions, middleware, and permissions are managed here.
 */

import { USER_TYPES } from '../constants/userTypes';

// Import all components
import Landing from '../pages/Landing'; import Terms from '../pages/legal/Terms';
import Privacy from '../pages/legal/Privacy';
import Contact from '../pages/Contact.jsx';
import About from '../pages/About.jsx';
import AboutSulhafaCare from '../components/Landing/AboutSulhafaCare.jsx';
import AboutSulhafaShop from '../components/Landing/AboutSulhafaShop.jsx';
import AboutSulhafaConnect from '../components/Landing/AboutSulhafaConnect.jsx';
import Partners from '../pages/Partners.jsx';

// Auth components
import SelectUserType from '../pages/auth/SelectUserType';
import Register from '../pages/auth/Register';
import Login from '../pages/auth/Login';
import CreatePassword from '../pages/auth/CreatePassword';
import CompleteProfile from '../pages/auth/CompleteProfile';
import GoogleRegister from '../pages/auth/GoogleRegister';
import GoogleCallback from '../pages/auth/GoogleCallback';
import AppleRegister from '../pages/auth/AppleRegister';
import AppleCallback from '../pages/auth/AppleCallback';
import ResetPasswordPage from '../pages/auth/ResetPasswordPage';
import AcceptClinicInvitation from '../pages/auth/AcceptClinicInvitation';
import TokenLogin from '../pages/auth/TokenLogin';

// Admin components
import AdminDashboard from '../pages/admin/AdminDashboard';
import DevAdminPage from '../pages/admin/DevAdminPage';
import SystemDashboard from '../pages/SystemDashboard/SystemDashboard';


// Pet Guardian components
import MyPetsDashboard from '../pages/pets/MyPetsDash';
import PetProfile from '../pages/pets/PetProfile';
import OthersPetProfile from '../pages/pets/OthersPetProfile';
import AddPet from '../pages/pets/AddPet';
import EditPet from '../pages/pets/EditPet';
import GuardianDash from '../pages/medical/GuardianDash';
import UrgentCaseSubmit from '../pages/medical/urgentcase-submit';
import EmergencyBroadcast from '../pages/medical/emergency-broadcast';
import AppointmentRequest from '../pages/medical/AppointmentRequest';

// Veterinarian components
import AddClinic from '../pages/medical/addclinic';
import ManageTeam from '../pages/medical/manage-team';
import Services from '../pages/medical/Sevices';
import AddService from '../pages/medical/Add-Services';
import ClinicInformation from '../pages/medical/clinic-information';
import MainReceptionPage from '../pages/reception/MainReceptionPage';

// Social components
import SocialFeed from '../pages/social/SocialFeed';
import UserProfile from '../pages/social/UserProfile';
import UserProfileEdit from '../pages/social/UserProfileEdit';
import SearchInterface from '../pages/social/SearchInterface';
import OtherUsersProfile from '../pages/social/OtherUsersProfile';
import BlockedUsers from '../pages/social/BlockedUsers';
import ChannelListPage from '../pages/social/ChannelListPage';
import ChannelDetailPage from '../pages/social/ChannelDetailPage';
import AnnouncementDetailPage from '../pages/social/AnnouncementDetailPage';
// Media page removed

// Adoption components
import AdoptionPage from '../pages/adoption/AdoptionPage';
import DashboardRedirect from '../pages/social/DashboardRedirect';


// Account components
import AccountPreferences from '../pages/account/AccountPreferences';
import NotificationSettings from '../pages/account/NotificationSettings';
import NotificationsPage from '../pages/account/NotificationsPage';
import PrivacySettings from '../pages/account/PrivacySettings';
import FinancialManagement from '../pages/account/FinancialManagement';
import AccountManagement from '../pages/account/AccountManagement';
import ChatPage from '../components/chat/ChatPage';
import RedirectToClinic from '../pages/medical/redirectToClinic';
import MarketplaceContainer from "../pages/marketplace/MarketplaceContainer";
// import ShopOwnerManagement from "../pages/marketplace/petshopownermarket/ShopOwnerManagement";


/**
 * SINGLE ROUTE DEFINITIONS OBJECT
 * 
 * This is the ONLY place to add new routes.
 * All routes are defined here with their middleware requirements.
 */
export const ROUTES = {
  // Public Routes - No authentication required
  '/': {
    component: Landing,
    middleware: { requireAuth: false }
  },
  '/about': {
    component: About,
    middleware: { requireAuth: false }
  },
  '/terms': {
    component: Terms,
    middleware: { requireAuth: false }
  },
  '/privacy': {
    component: Privacy,
    middleware: { requireAuth: false }
  },
  '/contact': {
    component: Contact,
    middleware: { requireAuth: false }
  },
  '/sulhafa-care': {
    component: AboutSulhafaCare,
    middleware: { requireAuth: false }
  },
  '/about/sulhafa-shop': {
    component: AboutSulhafaShop,
    middleware: { requireAuth: false }
  },
  '/sulhafa-connect': {
    component: AboutSulhafaConnect,
    middleware: { requireAuth: false }
  },
  '/our-partners': {
    component: Partners,
    middleware: { requireAuth: false }
  },

  // Authentication Routes - No authentication required
  '/auth/select-type': {
    component: SelectUserType,
    middleware: { requireAuth: false }
  },
  '/auth/login': {
    component: Login,
    middleware: { requireAuth: false }
  },
  '/auth/register/:userType': {
    component: Register,
    middleware: { requireAuth: false }
  },
  '/auth/google-register/:userType': {
    component: GoogleRegister,
    middleware: { requireAuth: false }
  },
  '/auth/google/callback': {
    component: GoogleCallback,
    middleware: { requireAuth: false }
  },
  '/auth/apple/callback': {
    component: AppleCallback,
    middleware: { requireAuth: false }
  },
  '/auth/apple-register/:userType': {
    component: AppleRegister,
    middleware: { requireAuth: false }
  },
  '/auth/create-password/:userType/:token': {
    component: CreatePassword,
    middleware: { requireAuth: false }
  },
  '/auth/create-password/:userType': {
    component: CreatePassword,
    middleware: { requireAuth: false }
  },
  '/auth/complete-profile/:userType': {
    component: CompleteProfile,
    middleware: { requireAuth: false }
  },
  '/reset-password': {
    component: ResetPasswordPage,
    middleware: { requireAuth: false }
  },
  '/auth/accept-clinic-invitation/:token': {
    component: AcceptClinicInvitation,
    middleware: { requireAuth: false }
  },
  '/auth/token-login': {
    component: TokenLogin,
    middleware: { requireAuth: false }
  },

  // Admin Routes
  '/admin/dashboard': {
    component: AdminDashboard,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.ADMIN, USER_TYPES.CONTENT_ADMIN]
    }
  },
  '/admin/dashboard/clinics': {
    component: AdminDashboard,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.ADMIN]
    }
  },
  '/admin/dashboard/bulk-messages': {
    component: AdminDashboard,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.ADMIN]
    }
  },
  '/admin/dashboard/announcements': {
    component: AdminDashboard,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.ADMIN]
    }
  },
  '/admin/dashboard/app-version': {
    component: AdminDashboard,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.ADMIN, USER_TYPES.CONTENT_ADMIN]
    }
  },
  '/admin/dashboard/audit-logs': {
    component: AdminDashboard,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.ADMIN]
    }
  },
  '/admin/dashboard/user-activity': {
    component: AdminDashboard,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.ADMIN]
    }
  },
  '/admin/dashboard/moderation/users': {
    component: AdminDashboard,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.ADMIN]
    }
  },
  '/admin/dashboard/moderation/users/reported': {
    component: AdminDashboard,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.ADMIN]
    }
  },
  '/admin/dashboard/moderation/users/suspended': {
    component: AdminDashboard,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.ADMIN]
    }
  },
  '/admin/dashboard/moderation/users/muted': {
    component: AdminDashboard,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.ADMIN]
    }
  },
  '/admin/dashboard/moderation/users/banned': {
    component: AdminDashboard,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.ADMIN]
    }
  },
  '/admin/dashboard/moderation/posts/all': {
    component: AdminDashboard,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.ADMIN]
    }
  },
  '/admin/dashboard/moderation/posts/reported': {
    component: AdminDashboard,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.ADMIN]
    }
  },
  '/admin/dashboard/cms/banners': {
    component: AdminDashboard,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.ADMIN, USER_TYPES.CONTENT_ADMIN]
    }
  },
  '/admin/dashboard/cms/referral-reports': {
    component: AdminDashboard,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.ADMIN]
    }
  },
  '/admin/dashboard/cms/banners/add': {
    component: AdminDashboard,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.ADMIN, USER_TYPES.CONTENT_ADMIN]
    }
  },
  '/admin/dashboard/cms/banners/edit/:id': {
    component: AdminDashboard,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.ADMIN, USER_TYPES.CONTENT_ADMIN]
    }
  },
  '/admin/dashboard/cms/breeds': {
    component: AdminDashboard,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.ADMIN, USER_TYPES.CONTENT_ADMIN]
    }
  },
  '/admin/dashboard/cms/breeds/add': {
    component: AdminDashboard,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.ADMIN, USER_TYPES.CONTENT_ADMIN]
    }
  },
  '/admin/dashboard/cms/advertisements': {
    component: AdminDashboard,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.ADMIN, USER_TYPES.CONTENT_ADMIN]
    }
  },
  '/admin/dashboard/cms/advertisements/:countryCode': {
    component: AdminDashboard,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.ADMIN, USER_TYPES.CONTENT_ADMIN]
    }
  },
  '/admin/dashboard/cms/advertisements/:countryCode/add': {
    component: AdminDashboard,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.ADMIN, USER_TYPES.CONTENT_ADMIN]
    }
  },
  '/admin/dashboard/cms/advertisements/:countryCode/edit/:advertisement_id': {
    component: AdminDashboard,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.ADMIN, USER_TYPES.CONTENT_ADMIN]
    }
  },
  '/admin/dashboard/cms/advertisements/add': {
    component: AdminDashboard,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.ADMIN, USER_TYPES.CONTENT_ADMIN]
    }
  },
  '/admin/dashboard/cms/advertisements/edit/:id': {
    component: AdminDashboard,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.ADMIN, USER_TYPES.CONTENT_ADMIN]
    }
  },
  '/admin/dashboard/cms/species': {
    component: AdminDashboard,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.ADMIN, USER_TYPES.CONTENT_ADMIN]
    }
  },
  '/admin/dashboard/cms/species/add': {
    component: AdminDashboard,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.ADMIN, USER_TYPES.CONTENT_ADMIN]
    }
  },
  '/admin/dashboard/data-entry/species': {
    component: AdminDashboard,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.ADMIN]
    }
  },
  '/admin/dashboard/data-entry/breeds': {
    component: AdminDashboard,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.ADMIN]
    }
  },
  '/dev-admin': {
    component: DevAdminPage,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.DEV_ADMIN]
    }
  },
  '/statistics': {
    component: SystemDashboard,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.ADMIN, USER_TYPES.DEV_ADMIN, USER_TYPES.ORGANIZATION_ADMIN]
    }
  },

  // Pet Guardian Routes - Protected (also accessible to SERVICE_PROVIDER)
  '/dashboard/pets': {
    component: MyPetsDashboard,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.PET_GUARDIAN, USER_TYPES.VETERINARIAN, USER_TYPES.SERVICE_PROVIDER, USER_TYPES.FIRM]
    }
  },
  '/dashboard': {
    component: DashboardRedirect,
    middleware: {
      requireAuth: true,
      allowedUserTypes: Object.values(USER_TYPES)
    }
  },
  '/pets/add': {
    component: AddPet,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.PET_GUARDIAN, USER_TYPES.VETERINARIAN, USER_TYPES.SERVICE_PROVIDER, USER_TYPES.FIRM]
    }
  },
  '/pets/:petId/view': {
    component: PetProfile,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.PET_GUARDIAN, USER_TYPES.VETERINARIAN, USER_TYPES.SERVICE_PROVIDER, USER_TYPES.FIRM]
    }
  },
  '/pets/:petId/view/:tab': {
    component: PetProfile,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.PET_GUARDIAN, USER_TYPES.VETERINARIAN, USER_TYPES.SERVICE_PROVIDER, USER_TYPES.FIRM]
    }
  },
  '/pets/:petId/edit': {
    component: EditPet,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.PET_GUARDIAN, USER_TYPES.VETERINARIAN, USER_TYPES.SERVICE_PROVIDER, USER_TYPES.FIRM]
    }
  },
  '/pets/view/others/:petId': {
    component: OthersPetProfile,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.PET_GUARDIAN, USER_TYPES.VETERINARIAN, USER_TYPES.SERVICE_PROVIDER, USER_TYPES.FIRM]
    }
  },
  '/pets/view/others/:petId/:tab': {
    component: OthersPetProfile,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.PET_GUARDIAN, USER_TYPES.VETERINARIAN, USER_TYPES.SERVICE_PROVIDER, USER_TYPES.FIRM]
    }
  },
  '/medical/dashboard/petguardian': {
    component: GuardianDash,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.PET_GUARDIAN]
    }
  },
  '/medical/dashboard/petguardian/:tab': {
    component: GuardianDash,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.PET_GUARDIAN]
    }
  },
  '/medical/dashboard/petshopowner': {
    component: GuardianDash,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.SERVICE_PROVIDER]
    }
  },
  '/medical/dashboard/petshopowner/:tab': {
    component: GuardianDash,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.SERVICE_PROVIDER]
    }
  },
  '/medical/dashboard/petguardian/urgent-case-submit': {
    component: UrgentCaseSubmit,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.PET_GUARDIAN, USER_TYPES.SERVICE_PROVIDER]
    }
  },
  '/medical/dashboard/petshopowner/urgent-case-submit': {
    component: UrgentCaseSubmit,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.SERVICE_PROVIDER]
    }
  },
  '/medical/dashboard/petguardian/urgent-case-submit/:userId': {
    component: UrgentCaseSubmit,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.PET_GUARDIAN, USER_TYPES.SERVICE_PROVIDER]
    }
  },
  '/medical/dashboard/petshopowner/urgent-case-submit/:userId': {
    component: UrgentCaseSubmit,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.SERVICE_PROVIDER]
    }
  },
  '/medical/emergency-broadcast/:case_id': {
    component: EmergencyBroadcast,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.PET_GUARDIAN, USER_TYPES.SERVICE_PROVIDER]
    }
  },
  '/medical/dashboard/petguardian/appointment-request': {
    component: AppointmentRequest,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.PET_GUARDIAN, USER_TYPES.SERVICE_PROVIDER]
    }
  },
  '/medical/dashboard/petshopowner/appointment-request': {
    component: AppointmentRequest,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.SERVICE_PROVIDER]
    }
  },

  // Veterinarian Routes - Protected
  '/reception': {
    component: MainReceptionPage,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.VETERINARIAN, USER_TYPES.CLINIC_MODERATOR]
    }
  },
  '/medical/clinicsetup': {
    component: AddClinic,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.VETERINARIAN, USER_TYPES.CLINIC_MODERATOR]
    }
  },
  '/medical/clinic/manage-team': {
    component: ManageTeam,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.VETERINARIAN]
    }
  },
  '/medical/clinic/services': {
    component: Services,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.VETERINARIAN]
    }
  },
  '/medical/clinic/services/add-service': {
    component: AddService,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.VETERINARIAN]
    }
  },
  '/medical/clinic/services/edit/': {
    component: AddService,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.VETERINARIAN]
    }
  },
  // New routes with clinicId parameter for appointment management
  '/medical/clinic/:clinicId/appointment-management/outpatient/:outpatientId': {
    component: ClinicInformation,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.VETERINARIAN, USER_TYPES.CLINIC_MODERATOR]
    }
  },
  '/medical/clinic/:clinicId/appointment-management/inpatient/:inpatientId': {
    component: ClinicInformation,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.VETERINARIAN, USER_TYPES.CLINIC_MODERATOR]
    }
  },
  '/medical/clinic/:clinicId/appointment-management/:tabType': {
    component: ClinicInformation,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.VETERINARIAN, USER_TYPES.CLINIC_MODERATOR]
    }
  },
  // Backward compatibility routes (without clinicId) - will be handled by clinic-information.jsx
  '/medical/clinic/appointment-management/outpatient/:outpatientId': {
    component: ClinicInformation,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.VETERINARIAN, USER_TYPES.CLINIC_MODERATOR]
    }
  },
  '/medical/clinic/appointment-management/inpatient/:inpatientId': {
    component: ClinicInformation,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.VETERINARIAN, USER_TYPES.CLINIC_MODERATOR]
    }
  },
  '/medical/clinic/appointment-management/:tabType': {
    component: ClinicInformation,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.VETERINARIAN, USER_TYPES.CLINIC_MODERATOR]
    }
  },
  // Specific routes for chat (must come before :clinicId/:index routes to avoid "chat" being treated as an ID)
  '/medical/clinic/chat': {
    component: ClinicInformation,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.VETERINARIAN, USER_TYPES.CLINIC_MODERATOR]
    }
  },
  '/medical/clinic/chat/:receiverId': {
    component: ClinicInformation,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.VETERINARIAN, USER_TYPES.CLINIC_MODERATOR]
    }
  },
  // New routes with clinicId parameter
  '/medical/clinic/:clinicId/:index': {
    component: ClinicInformation,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.VETERINARIAN, USER_TYPES.CLINIC_MODERATOR]
    }
  },
  '/medical/clinic/:clinicId/dashboard/:view': {
    component: ClinicInformation,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.VETERINARIAN, USER_TYPES.CLINIC_MODERATOR]
    }
  },
  '/medical/clinic/:clinicId/:index/:receiverId': {
    component: ClinicInformation,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.VETERINARIAN]
    }
  },
  // Backward compatibility route - redirects to first owned clinic or default
  '/medical/clinic/:index': {
    component: ClinicInformation,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.VETERINARIAN, USER_TYPES.CLINIC_MODERATOR]
    }
  },
  '/medical/clinic/redirect-to-clinic': {
    component: RedirectToClinic,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.VETERINARIAN, USER_TYPES.CLINIC_MODERATOR]
    }
  },
  // Shared Routes - All authenticated users
  '/social': {
    component: SocialFeed,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.PET_GUARDIAN, USER_TYPES.VETERINARIAN, USER_TYPES.SERVICE_PROVIDER, USER_TYPES.FIRM]
    }
  },
  '/adoption': {
    component: AdoptionPage,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.PET_GUARDIAN, USER_TYPES.VETERINARIAN, USER_TYPES.SERVICE_PROVIDER, USER_TYPES.FIRM]
    }
  },
  '/social/feed': {
    component: SocialFeed,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.PET_GUARDIAN, USER_TYPES.VETERINARIAN, USER_TYPES.SERVICE_PROVIDER, USER_TYPES.FIRM]
    }
  },
  '/social/search': {
    component: SearchInterface,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.PET_GUARDIAN, USER_TYPES.VETERINARIAN, USER_TYPES.SERVICE_PROVIDER, USER_TYPES.FIRM]
    }
  },
  '/social/blocked-users': {
    component: BlockedUsers,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.PET_GUARDIAN, USER_TYPES.VETERINARIAN, USER_TYPES.SERVICE_PROVIDER, USER_TYPES.FIRM]
    }
  },
  '/social/profile': {
    component: UserProfile,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.PET_GUARDIAN, USER_TYPES.VETERINARIAN, USER_TYPES.SERVICE_PROVIDER, USER_TYPES.FIRM]
    }
  },
  '/social/profile/pets': {
    component: UserProfile,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.PET_GUARDIAN, USER_TYPES.VETERINARIAN, USER_TYPES.SERVICE_PROVIDER, USER_TYPES.FIRM]
    }
  },
  '/social/profile/buddies': {
    component: UserProfile,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.PET_GUARDIAN, USER_TYPES.VETERINARIAN, USER_TYPES.SERVICE_PROVIDER, USER_TYPES.FIRM]
    }
  },
  '/social/profile/communities': {
    component: UserProfile,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.PET_GUARDIAN, USER_TYPES.VETERINARIAN, USER_TYPES.SERVICE_PROVIDER, USER_TYPES.FIRM]
    }
  },
  '/social/profile/media/:postId?': {
    component: UserProfile,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.PET_GUARDIAN, USER_TYPES.VETERINARIAN, USER_TYPES.SERVICE_PROVIDER, USER_TYPES.FIRM]
    }
  },
  '/social/profile/posts/:postId?': {
    component: UserProfile,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.PET_GUARDIAN, USER_TYPES.VETERINARIAN, USER_TYPES.SERVICE_PROVIDER, USER_TYPES.FIRM]
    }
  },
  '/social/profile/:profileId': {
    component: OtherUsersProfile,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.PET_GUARDIAN, USER_TYPES.VETERINARIAN, USER_TYPES.SERVICE_PROVIDER, USER_TYPES.FIRM]
    }
  },
  '/social/profile/:profileId/pets': {
    component: OtherUsersProfile,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.PET_GUARDIAN, USER_TYPES.VETERINARIAN, USER_TYPES.SERVICE_PROVIDER, USER_TYPES.FIRM]
    }
  },
  '/social/profile/:profileId/buddies': {
    component: OtherUsersProfile,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.PET_GUARDIAN, USER_TYPES.VETERINARIAN, USER_TYPES.SERVICE_PROVIDER, USER_TYPES.FIRM]
    }
  },
  '/social/profile/:profileId/communities': {
    component: OtherUsersProfile,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.PET_GUARDIAN, USER_TYPES.VETERINARIAN, USER_TYPES.SERVICE_PROVIDER, USER_TYPES.FIRM]
    }
  },
  '/social/profile/:profileId/media/:postId?': {
    component: OtherUsersProfile,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.PET_GUARDIAN, USER_TYPES.VETERINARIAN, USER_TYPES.SERVICE_PROVIDER, USER_TYPES.FIRM]
    }
  },
  '/social/profile/:profileId/posts/:postId?': {
    component: OtherUsersProfile,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.PET_GUARDIAN, USER_TYPES.VETERINARIAN, USER_TYPES.SERVICE_PROVIDER, USER_TYPES.FIRM]
    }
  },
  '/profile/:profileId': {
    component: UserProfile,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.PET_GUARDIAN, USER_TYPES.VETERINARIAN, USER_TYPES.SERVICE_PROVIDER, USER_TYPES.FIRM]
    }
  },
  '/profile/:profileId/media/:postId?': {
    component: UserProfile,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.PET_GUARDIAN, USER_TYPES.VETERINARIAN, USER_TYPES.SERVICE_PROVIDER, USER_TYPES.FIRM]
    }
  },
  '/profile/:profileId/posts/:postId?': {
    component: UserProfile,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.PET_GUARDIAN, USER_TYPES.VETERINARIAN, USER_TYPES.SERVICE_PROVIDER, USER_TYPES.FIRM]
    }
  },
  '/profile/edit': {
    component: UserProfileEdit,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.PET_GUARDIAN, USER_TYPES.VETERINARIAN, USER_TYPES.SERVICE_PROVIDER, USER_TYPES.FIRM]
    }
  },
  '/search': {
    component: SearchInterface,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.PET_GUARDIAN, USER_TYPES.VETERINARIAN, USER_TYPES.SERVICE_PROVIDER, USER_TYPES.FIRM]
    }
  },
  '/social/profile/:userId': {
    component: OtherUsersProfile,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.PET_GUARDIAN, USER_TYPES.VETERINARIAN, USER_TYPES.SERVICE_PROVIDER, USER_TYPES.FIRM]
    }
  },
  '/social/channels': {
    component: ChannelListPage,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.PET_GUARDIAN, USER_TYPES.VETERINARIAN, USER_TYPES.SERVICE_PROVIDER, USER_TYPES.FIRM]
    }
  },
  '/social/channels/:channelId': {
    component: ChannelDetailPage,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.PET_GUARDIAN, USER_TYPES.VETERINARIAN, USER_TYPES.SERVICE_PROVIDER, USER_TYPES.FIRM]
    }
  },
  '/social/announcement': {
    component: AnnouncementDetailPage,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.PET_GUARDIAN, USER_TYPES.VETERINARIAN, USER_TYPES.SERVICE_PROVIDER]
    }
  },
  // Removed legacy media route
  '/chat/:receiverId': {
    component: ChatPage,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.PET_GUARDIAN, USER_TYPES.VETERINARIAN, USER_TYPES.SERVICE_PROVIDER, USER_TYPES.FIRM]
    }
  },
  '/chat': {
    component: ChatPage,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.PET_GUARDIAN, USER_TYPES.VETERINARIAN, USER_TYPES.SERVICE_PROVIDER, USER_TYPES.FIRM]
    }
  },
  '/chat/:conversationId/:conversationType': {
    component: ChatPage,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.PET_GUARDIAN, USER_TYPES.VETERINARIAN, USER_TYPES.SERVICE_PROVIDER, USER_TYPES.FIRM]
    }
  },
  '/account/preferences': {
    component: AccountPreferences,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.PET_GUARDIAN, USER_TYPES.VETERINARIAN, USER_TYPES.SERVICE_PROVIDER, USER_TYPES.FIRM]
    }
  },
  '/account/preferences/:tab': {
    component: AccountPreferences,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.PET_GUARDIAN, USER_TYPES.VETERINARIAN, USER_TYPES.SERVICE_PROVIDER, USER_TYPES.FIRM]
    }
  },
  '/account/profile': {
    component: AccountPreferences,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.PET_GUARDIAN, USER_TYPES.VETERINARIAN, USER_TYPES.SERVICE_PROVIDER, USER_TYPES.FIRM]
    }
  },
  '/account/notifications/settings': {
    component: NotificationSettings,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.PET_GUARDIAN, USER_TYPES.VETERINARIAN, USER_TYPES.SERVICE_PROVIDER, USER_TYPES.FIRM]
    }
  },
  '/notifications': {
    component: NotificationsPage,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.PET_GUARDIAN, USER_TYPES.VETERINARIAN, USER_TYPES.SERVICE_PROVIDER, USER_TYPES.CLINIC_MODERATOR, USER_TYPES.FIRM]
    }
  },
  '/dashboard/pet-guardian/notifications': {
    component: NotificationsPage,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.PET_GUARDIAN]
    }
  },
  '/dashboard/veterinarian/notifications': {
    component: NotificationsPage,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.VETERINARIAN]
    }
  },
  '/dashboard/pet-shop-owner/notifications': {
    component: NotificationsPage,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.SERVICE_PROVIDER]
    }
  },
  '/dashboard/service-provider/notifications': {
    component: NotificationsPage,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.SERVICE_PROVIDER] // Backend uses SERVICE_PROVIDER, but route path shows "service-provider" for UI consistency
    }
  },
  '/dashboard/firm/notifications': {
    component: NotificationsPage,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.FIRM]
    }
  },
  '/account/management/settings': {
    component: AccountManagement,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.PET_GUARDIAN, USER_TYPES.VETERINARIAN, USER_TYPES.SERVICE_PROVIDER]
    }
  },
  '/account/financial/settings': {
    component: FinancialManagement,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.PET_GUARDIAN, USER_TYPES.VETERINARIAN, USER_TYPES.SERVICE_PROVIDER]
    }
  },
  '/account/privacy/settings': {
    component: PrivacySettings,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.PET_GUARDIAN, USER_TYPES.VETERINARIAN, USER_TYPES.SERVICE_PROVIDER]
    }
  },

  // Marketplace Routes
  '/marketplace': {
    component: MarketplaceContainer,
    middleware: {
      requireAuth: true,
      allowedUserTypes: [USER_TYPES.PET_GUARDIAN, USER_TYPES.VETERINARIAN, USER_TYPES.SERVICE_PROVIDER, USER_TYPES.CLINIC_MODERATOR]
    }
  },
  // '/marketplace/manageshop': {
  //   component: ShopOwnerManagement,
  //   middleware: { 
  //     requireAuth: true, 
  //     allowedUserTypes: [USER_TYPES.SERVICE_PROVIDER] 
  //   }
  // },
};

// Redirect routes
export const REDIRECT_ROUTES = {
  '/dashboard/pet-guardian': '/dashboard',
  '/dashboard/veterinarian': '/dashboard',
  '/dashboard/pet-shop-owner': '/dashboard',
  '/auth': '/auth/select-type',
  '/admin/dashboard/clinics/all': '/admin/dashboard/clinics',
  '/admin/dashboard/clinics/verified': '/admin/dashboard/clinics',
  '/admin/dashboard/clinics/rejected': '/admin/dashboard/clinics',
  '/admin/dashboard/clinics/new': '/admin/dashboard/clinics',
  '/admin/dashboard/clinics/upgrade-requests': '/admin/dashboard/clinics',
  '/admin/dashboard/paid': '/admin/dashboard/clinics'
};

/**
 * UTILITY FUNCTIONS
 * 
 * These functions help manage routes but you don't need to modify them.
 * Just add new routes to the ROUTES object above.
 */

// Get all routes as an array
export const getAllRoutes = () => {
  return Object.entries(ROUTES).map(([path, config]) => ({
    path,
    ...config
  }));
};

// Get routes by user type
export const getRoutesByUserType = (userType) => {
  return Object.entries(ROUTES)
    .filter(([_, config]) =>
      !config.middleware.requireAuth ||
      config.middleware.allowedUserTypes.includes(userType)
    )
    .map(([path, config]) => ({ path, ...config }));
};

// Get public routes
export const getPublicRoutes = () => {
  return Object.entries(ROUTES)
    .filter(([_, config]) => !config.middleware.requireAuth)
    .map(([path, config]) => ({ path, ...config }));
};

// Get protected routes
export const getProtectedRoutes = () => {
  return Object.entries(ROUTES)
    .filter(([_, config]) => config.middleware.requireAuth)
    .map(([path, config]) => ({ path, ...config }));
};

// Check if a route exists
export const routeExists = (path) => {
  return ROUTES.hasOwnProperty(path);
};

// Get route configuration
export const getRouteConfig = (path) => {
  return ROUTES[path] || null;
};
